import { AnyAction, Middleware } from 'redux';
import { selectIsHitl7Enabled } from '../redux/features/config/config.selector';
import { v4 as uuidv4 } from 'uuid';
import { DUMMY_RESTAURANT_CODE } from '../constants';
import {
  AgentTypes,
  EventTypes,
  TTS_PRIORITY_BY_INTENT,
  TTS_PRIORITY_DEFAULT,
} from '../constants/event';
import {
  getSessionBufferMessages,
  InfoTransmissionMessage,
  messagingActions,
  sendNetworkCallLogs,
  setEventWSConnectionDuration,
  setAudioWSConnectionDuration,
} from '../reducers/messagingSlice';
import {
  handleAIUpsellAttemptMetric,
  handleUpsellAttemptMetricOnTTSAcknowledged,
} from '../reducers/messagingSlice.utils';
import { checkHealthStatus } from '../redux/features/healthStatusCheck/healthStatusCheck.slice';
import { readEnvVariable } from '../utils';
import { agent } from '../utils/agent';
import { Counter } from '../utils/counter';
import logger from '../utils/logger';
import { getWebsocketUrl, sleep } from '../utils/network';
import { isAudioMessage, isGuestAudioFrame } from '../utils/audioRouting';
import { audioPlayer } from '../utils/asrcAudio';
import {
  AudioSilenceWatchdog,
  AUDIO_SILENCE_THRESHOLD_MS,
} from '../utils/audioSilenceWatchdog';
import { cappedJson } from '../utils/cappedJson';
import { PerfTimer } from '../utils/timer';
import { RootStore } from './store';
import { CriticalChecksNames } from '../redux/features/healthScore/healthScore.constants';
import { updateCriticalCheckScore } from '../redux/features/healthScore/healthScore.slice';
import { AIStatusColors } from '../redux/features/ai/ai.constants';
import { WebSocketCloseCode } from '../constants/enums';
import { buildAndDispatchOrderProcessingMetric } from '../reducers/orderSlice.utils';
import { selectIsAgentActionBlocked } from '../redux/features/agentMode/agentMode.selector';

const isMatchMessagingAction = (action: AnyAction) => {
  const {
    sendMessage,
    sendInfo,
    sendOrder,
    sendAgentCart,
    sendLoyalty,
    sendEndSession,
    sendTTSRequest,
    sendError,
    sendAgentFirstActivity,
    sendAgentFirstInterception,
    sendAgentInterception,
    sendAgentMetric,
    sendAgentSuggestionResponse,
    sendStaffInterception,
    sendHITLSessionEnd,
    sendHITLSessionStart,
    sendHITLEventConnection,
    sendCancelOrder,
  } = messagingActions;
  return (
    sendMessage.match(action) ||
    sendInfo.match(action) ||
    sendOrder.match(action) ||
    sendAgentCart.match(action) ||
    sendLoyalty.match(action) ||
    sendEndSession.match(action) ||
    sendTTSRequest.match(action) ||
    sendError.match(action) ||
    sendAgentFirstActivity.match(action) ||
    sendAgentFirstInterception.match(action) ||
    sendAgentInterception.match(action) ||
    sendAgentMetric.match(action) ||
    sendAgentSuggestionResponse.match(action) ||
    sendStaffInterception.match(action) ||
    sendHITLSessionEnd.match(action) ||
    sendHITLSessionStart.match(action) ||
    sendHITLEventConnection.match(action) ||
    sendCancelOrder.match(action)
  );
};

const socketMiddleware: Middleware = (store: RootStore) => {
  let eventSocket: WebSocket | undefined = undefined; // Websocket of event backend
  let audioSocket: WebSocket | undefined = undefined; // Websocket of audio backend

  let eventWebSocketConnectionTimer = new PerfTimer({ autoStart: false });
  let audioWebSocketConnectionTimer = new PerfTimer({ autoStart: false });

  let audioWebSocketRetries = 1; // Count of event backend websocket retry
  let eventWebSocketRetries = 1; // Count of audio backend websocket retry

  // Timer cleanup references for event websocket
  let eventMessageInterval: NodeJS.Timeout | null = null;
  let eventHeartbeatInterval: NodeJS.Timeout | null = null;

  // Timer cleanup references for audio websocket
  let audioMessageInterval: NodeJS.Timeout | null = null;
  let audioHeartbeatInterval: NodeJS.Timeout | null = null;

  // HITL-1511: flags an audio socket that is open during an active session
  // but delivering no frames (the backend streams ~50/sec even for silence,
  // so a 10s gap means routing is broken, not that the drive-thru is quiet).
  const audioSilenceWatchdog = new AudioSilenceWatchdog();

  const HEARTBEAT_INTERVAL = 10000; // Send ping every 10 seconds

  // Helper function to clean up event websocket intervals
  const clearEventWebSocketIntervals = () => {
    if (eventMessageInterval) {
      clearInterval(eventMessageInterval);
      eventMessageInterval = null;
    }
    if (eventHeartbeatInterval) {
      clearInterval(eventHeartbeatInterval);
      eventHeartbeatInterval = null;
    }
  };

  // Helper function to clean up audio websocket intervals
  const clearAudioWebSocketIntervals = () => {
    if (audioMessageInterval) {
      clearInterval(audioMessageInterval);
      audioMessageInterval = null;
    }
    if (audioHeartbeatInterval) {
      clearInterval(audioHeartbeatInterval);
      audioHeartbeatInterval = null;
    }
    // Every close/reconnect path funnels through here; those paths have their
    // own logging, so an open silence gap is dropped rather than reported.
    audioSilenceWatchdog.setArmed(false, Date.now());
  };

  // Helper function to setup heartbeat interval
  const setupHeartbeat = (
    selectedRestaurantCode: string,
    isAudioWS: boolean,
    socket: WebSocket | undefined
  ) => {
    const interval = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        const { sessionBoundary, messages, taskRouter, user } =
          store.getState() || {};
        const { email, username, id, firstName, lastName } =
          user?.userProfile || {};
        try {
          const pingPayload = JSON.stringify({
            type: 'ping',
            id: uuidv4(),
            ...(id && {
              metadata: {
                agent_name: username,
                agent_id: id,
                agent_email: email,
                agent_first_name: firstName,
                agent_last_name: lastName,
              },
            }),
            session_id:
              taskRouter?.taskAssignedSessionId ||
              sessionBoundary?.sessionId ||
              sessionBoundary?.endedSessionId ||
              messages?.currentSessionId,
          });
          socket.send(pingPayload);
        } catch (error) {
          logger.warn({
            restaurantCode: selectedRestaurantCode,
            message: `${
              isAudioWS ? 'Audio' : 'Event'
            } websocket ping failed to send`,
            moreInfo: JSON.stringify(error, Object.getOwnPropertyNames(error)),
          });
        }
      }
    }, HEARTBEAT_INTERVAL);

    if (isAudioWS) {
      audioHeartbeatInterval = interval;
    } else {
      eventHeartbeatInterval = interval;
    }
  };

  const shouldRetryConnection = function (event: any) {
    return (
      event.code !== WebSocketCloseCode.NORMAL_CLOSURE &&
      event.code !== WebSocketCloseCode.GOING_AWAY
    );
  };

  const handleWebSocketClose = ({
    event,
    isAudioWS,
    selectedRestaurantCode,
    onConnectionLost,
    onCloseConnection,
    retryConnection,
  }: {
    event: any;
    isAudioWS: boolean;
    selectedRestaurantCode: string;
    onConnectionLost: () => void;
    onCloseConnection: () => void;
    retryConnection: (params: {
      isAudioWS: boolean;
      maxRetries?: number;
    }) => void;
  }) => {
    const wsType = isAudioWS ? 'Audio' : 'Event';

    if (shouldRetryConnection(event)) {
      logger.warn({
        restaurantCode: selectedRestaurantCode,
        message: `${wsType} websocket closed abnormally with code ${event.code}, attempting reconnect`,
      });
      onConnectionLost();
      // HITL-1743: surface the drop now — reconnect() sleeps its backoff before
      // the first "Reconnecting... (Attempt N/5)" message, so without this the
      // banner lagged the drop by 2–4s and a quick recovery showed nothing.
      // Only on the first close of an episode: a failed attempt also lands here,
      // and must not overwrite the attempt counter with this message.
      const retries = isAudioWS ? audioWebSocketRetries : eventWebSocketRetries;
      if (retries === 1 && store.getState()?.user?.isLoggedIn) {
        store.dispatch(
          isAudioWS
            ? messagingActions.setAudioWSConnectionMessage(
                'Connection lost, reconnecting...'
              )
            : messagingActions.setEventWSConnectionMessage(
                'Connection lost, reconnecting...'
              )
        );
      }
      retryConnection({ isAudioWS, maxRetries: 5 });
    } else {
      logger.info({
        restaurantCode: selectedRestaurantCode,
        message: `${wsType} websocket closed gracefully with code ${event.code}, not reconnecting`,
      });
      onCloseConnection();
    }
  };

  return (next) => (action) => {
    const {
      restaurant: {
        selectedRestaurantDetails: { restaurantCode: selectedRestaurantCode },
        selectedStage,
      },
      config: { WEBSOCKET_RETRY_MAX_BACKOFF },
      messages: { isConnected },
      user,
      config,
      taskRouter: { taskAssignedSessionId },
    } = store.getState();

    const isEventConnectionEstablished = eventSocket && isConnected;

    const reconnect = async ({
      isAudioWS,
      maxRetries = 9,
    }: {
      isAudioWS: boolean;
      maxRetries?: number;
    }) => {
      if (!store.getState()?.user?.isLoggedIn) {
        return;
      }
      let retries = isAudioWS ? audioWebSocketRetries : eventWebSocketRetries;
      // HITL-1743: the counter starts at 1, so `<=` is what makes maxRetries
      // attempts (labelled 1/N..N/N) before the "after N attempts" failure.
      if (retries <= maxRetries) {
        let multiple = 1 + Math.random(); // multiple is in the range 1+[0 to 1] = [1 to 2]
        let timeToWait = 2 ** retries * 1000 * multiple;
        await sleep(
          timeToWait > WEBSOCKET_RETRY_MAX_BACKOFF
            ? WEBSOCKET_RETRY_MAX_BACKOFF
            : timeToWait
        );

        const isWSEstablished = isAudioWS
          ? audioSocket && audioSocket.readyState !== audioSocket.CLOSED
          : eventSocket && eventSocket.readyState !== eventSocket.CLOSED;

        if (isWSEstablished || !store.getState()?.user?.isLoggedIn) {
          return;
        }
        // reconnect if lost connection
        logger.warn({
          restaurantCode: selectedRestaurantCode,
          message: `In ${retries} retry to established connection to restaurant specific ${
            isAudioWS ? 'audio' : ''
          } websocket`,
        });

        // HITL-1492: record the reconnect ATTEMPT so the frozen issue-report context
        // can carry it (an `Audio - Choppy` ranking signal). Write-only — the retry
        // counters above remain the single source of reconnect BEHAVIOUR; this only
        // makes the count visible to selectors, which closure variables are not.
        //
        // This runs once per attempt, before the connect below, and the loop re-enters
        // here through `onclose` when that connect fails — so a single flaky episode
        // records several. Deliberate: a session that never recovers is the worst case
        // for audio quality and would count ZERO if this moved to the success path.
        // The field is named `wsReconnectAttempts` to say so.
        store.dispatch(
          messagingActions.wsReconnectAttemptRecorded({
            socket: isAudioWS ? 'audio' : 'event',
          })
        );

        if (isAudioWS) {
          store.dispatch(
            messagingActions.setAudioWSConnectionMessage(
              `Reconnecting audio websocket... (Attempt ${audioWebSocketRetries}/${maxRetries})`
            )
          );
          audioWebSocketRetries += 1;
          audioConnect();
        } else {
          store.dispatch(
            messagingActions.setEventWSConnectionMessage(
              `Reconnecting event websocket... (Attempt ${eventWebSocketRetries}/${maxRetries})`
            )
          );
          eventWebSocketRetries += 1;
          eventConnect();
        }
      } else {
        logger.error({
          restaurantCode: selectedRestaurantCode,
          message: `Reached  maximum retry of ${maxRetries} to established connection to restaurant specific ${
            isAudioWS ? 'audio' : ''
          } websocket`,
        });

        // Notify user that max retries reached
        if (isAudioWS) {
          store.dispatch(
            messagingActions.setAudioWSConnectionMessage(
              `Failed to reconnect audio websocket after ${maxRetries} attempts. Please check your connection or refresh browser`
            )
          );
        } else {
          store.dispatch(
            messagingActions.setEventWSConnectionMessage(
              `Failed to reconnect event websocket after ${maxRetries} attempts. Please check your connection or or refresh browser`
            )
          );
        }

        store.dispatch(
          updateCriticalCheckScore({
            name: isAudioWS
              ? CriticalChecksNames.wsRestaurantAudio
              : CriticalChecksNames.wsRestaurantEvents,
            value: 0,
          })
        );
      }
    };

    const eventConnect = () => {
      const username = user?.userProfile?.username;
      if (!selectedRestaurantCode || !username) {
        return;
      }
      agent.setAgentId(username);

      if (eventSocket) {
        eventSocket.close();
      }

      clearEventWebSocketIntervals();

      const eventWebSocketURL = getWebsocketUrl(
        config,
        selectedRestaurantCode,
        selectedStage,
        false
      );

      eventWebSocketConnectionTimer.start();
      eventSocket = new WebSocket(eventWebSocketURL);

      eventSocket.onerror = () => {
        logger.warn({
          restaurantCode: selectedRestaurantCode,
          message: 'Restaurant specific websocket encountered an error',
        });
      };

      eventSocket.onopen = () => {
        logger.info({
          restaurantCode: selectedRestaurantCode,
          message:
            'Successfully established connection to restaurant specific websocket',
        });

        const eventConnectionDuration = eventWebSocketConnectionTimer.stop();
        store.dispatch(messagingActions.connectionEstablished());
        store.dispatch(setEventWSConnectionDuration(eventConnectionDuration));
        setupHeartbeat(selectedRestaurantCode, false, eventSocket);
        if (eventWebSocketRetries > 1) {
          store.dispatch(
            messagingActions.setEventWSConnectionMessage(
              'Event websocket reconnected successfully'
            )
          );
          store.dispatch(messagingActions.setEventWSConnectionSuccess(true));
        }
        eventWebSocketRetries = 1;

        const socketUrl = eventWebSocketURL.substring(
          0,
          eventWebSocketURL.indexOf('HITL_100') + 'HITL_100'.length
        );
        store.dispatch(
          sendNetworkCallLogs({
            url: socketUrl,
            duration: eventConnectionDuration,
            message:
              'restaurant specific websocket connection established successfully',
            via: 'websocket',
          })
        );

        const payload: Partial<InfoTransmissionMessage> = {
          data: { type: 'METRIC', message: 'Test event websocket signal' },
        };
        store.dispatch(messagingActions.sendInfo(payload as any));
        store.dispatch(
          updateCriticalCheckScore({
            name: CriticalChecksNames.wsRestaurantEvents,
            value: 1,
          })
        );

        const {
          taskRouter: { isCurrentTaskUnsupervised, currentTaskId },
          ai: { isAIAutoMode, aiStatus },
        } = store.getState();
        if (
          currentTaskId &&
          !isCurrentTaskUnsupervised &&
          isAIAutoMode &&
          aiStatus.status === AIStatusColors.green
        ) {
          // Check AI messages in case event websocket is not established before AI escalation on car enter on TR supervised AI mode
          store.dispatch(getSessionBufferMessages({ checkAIMessages: true }));
        }
      };

      let messages: any[] = [];
      eventSocket.onmessage = (event: any) => {
        const data = JSON.parse(event.data);
        if (data) {
          messages.push(data);
        }
      };

      eventMessageInterval = setInterval(() => {
        if (!messages.length) {
          return;
        }

        const batch = messages;
        messages = [];

        const { isPlaying } = store.getState().messages;

        // WS-1 (HITL-1411): guest (ORDERBOARD) audio is played locally and never
        // dispatched into Redux; non-guest audio is dropped. Only non-audio
        // events flow on. Live audio normally arrives on the audio socket, but
        // the event socket is handled the same way so no audio reaches the
        // reducer (which no longer plays audio).
        const nonAudioMessages: any[] = [];

        batch.forEach((message) => {
          if (
            taskAssignedSessionId &&
            message.session_id !== taskAssignedSessionId
          ) {
            return;
          }

          if (message.event === EventTypes.audio) {
            if (
              isGuestAudioFrame(message) &&
              isPlaying &&
              audioPlayer.audioContext
            ) {
              audioPlayer.pushBase64(message.data.payload);
            }
            return; // audio handled locally; never forwarded to Redux
          }

          // Handle upsell metric for ORDERBOARD text events when TTS is acknowledged.
          if (message.event === EventTypes.text) {
            handleUpsellAttemptMetricOnTTSAcknowledged(
              message,
              store.dispatch as any
            );
          }

          // HITL-1701: the AI recording an upsell attempt of its own.
          handleAIUpsellAttemptMetric(message);

          nonAudioMessages.push(message);
        });

        if (!nonAudioMessages.length) {
          return;
        }

        const hasNotableEvent = nonAudioMessages.some(
          (m: any) =>
            m?.event === EventTypes.check || m?.event === EventTypes.error
        );
        logger.debug({
          restaurantCode: selectedRestaurantCode,
          message:
            'Process non-audio message via restaurant specific websocket',
          count: nonAudioMessages.length,
          events: nonAudioMessages.map((m: any) => m?.event),
          // WS-3 (HITL-1411): the full (capped) payload is heavy and this fires
          // on every ~100ms flush, so only attach it when the batch carries a
          // notable event (final check / error); otherwise a count + event-type
          // summary is enough.
          ...(hasNotableEvent
            ? { moreInfo: cappedJson(nonAudioMessages) }
            : {}),
        });

        const rootState = store.getState();
        const {
          taskRouter,
          sessionBoundary,
          ai,
          config: {
            FEATURE_FLAG_FINAL_ORDER_VALIDATION,
            FEATURE_FLAG_LOYALTY_SUPPORT,
            FEATURE_FLAG_MASK_ORDER_PENDING_ERROR,
            FEATURE_FLAG_SECONDARY_ASR_ENABLED,
          },
        } = rootState;
        store.dispatch(
          messagingActions.messageReceived({
            messages: nonAudioMessages,
            taskRouter,
            sessionBoundarySessionId: sessionBoundary.sessionId,
            ai,
            FEATURE_FLAG_FINAL_ORDER_VALIDATION,
            FEATURE_FLAG_LOYALTY_SUPPORT,
            FEATURE_FLAG_MASK_ORDER_PENDING_ERROR,
            FEATURE_FLAG_SECONDARY_ASR_ENABLED,
            // HITL-1669: resolved here rather than in the reducer, so the
            // hitl_3_0 AND hitl_7_0 gate stays defined in one place.
            isHitl7Enabled: selectIsHitl7Enabled(rootState),
          })
        );
      }, 100);

      eventSocket.onclose = (event: any) => {
        if (eventSocket?.readyState === eventSocket?.CLOSED) {
          eventWebSocketConnectionTimer.stop();
        }
        // Clean up intervals on close
        clearEventWebSocketIntervals();

        handleWebSocketClose({
          event,
          isAudioWS: false,
          selectedRestaurantCode,
          onConnectionLost: () =>
            store.dispatch(messagingActions.connectionLost()),
          onCloseConnection: () =>
            store.dispatch(messagingActions.closeConnection()),
          retryConnection: reconnect,
        });
      };

      store.dispatch(checkHealthStatus());
    };

    const audioConnect = () => {
      const username = user?.userProfile?.username;

      if (!selectedRestaurantCode || !username) {
        return;
      }

      agent.setAgentId(username);

      if (audioSocket) {
        audioSocket.close();
      }

      clearAudioWebSocketIntervals();

      const audioWebSocketURL = getWebsocketUrl(
        config,
        selectedRestaurantCode,
        selectedStage,
        true
      );

      audioWebSocketConnectionTimer.start();
      audioSocket = new WebSocket(audioWebSocketURL);

      audioSocket.onerror = () => {
        logger.warn({
          restaurantCode: selectedRestaurantCode,
          message: 'Restaurant specific audio websocket encountered an error',
        });
      };

      audioSocket.onopen = () => {
        logger.info({
          restaurantCode: selectedRestaurantCode,
          message: `Successfully established connection to restaurant specific audio websocket`,
        });

        const audioConnectionDuration = audioWebSocketConnectionTimer.stop();
        store.dispatch(messagingActions.audioWSConnectionEstablished());
        store.dispatch(setAudioWSConnectionDuration(audioConnectionDuration));
        setupHeartbeat(selectedRestaurantCode, true, audioSocket);
        // Show success message if reconnection succeeded after retries
        if (audioWebSocketRetries > 1) {
          store.dispatch(
            messagingActions.setAudioWSConnectionMessage(
              'Audio websocket reconnected successfully'
            )
          );
          store.dispatch(messagingActions.setAudioWSConnectionSuccess(true));
        }

        audioWebSocketRetries = 1;
        const audioUrl = audioWebSocketURL.substring(
          0,
          audioWebSocketURL.indexOf('HITL_100') + 'HITL_100'.length
        );
        store.dispatch(
          sendNetworkCallLogs({
            url: audioUrl,
            duration: audioConnectionDuration,
            message: `Restaurant specific audio websocket connection established successfully`,
            via: 'websocket',
          })
        );

        const payload: Partial<InfoTransmissionMessage> = {
          data: {
            type: 'METRIC',
            message: `Test audio websocket signal`,
          },
        };
        store.dispatch(messagingActions.sendInfo(payload as any));
        store.dispatch(
          updateCriticalCheckScore({
            name: CriticalChecksNames.wsRestaurantAudio,
            value: 1,
          })
        );
      };

      let messages: any[] = [];
      // HITL-1471: WS-2 backpressure removed. No audio frame is dropped here;
      // a long buffer is drained by the ASRC worklet's own catch-up
      // (asrc-processor.js), which is what handled it before HITL-1411.
      audioSocket.onmessage = (event: any) => {
        const data = JSON.parse(event.data);
        if (data) {
          if (isAudioMessage(data)) {
            const resumed = audioSilenceWatchdog.noteFrame(Date.now());
            if (resumed?.type === 'recovered') {
              logger.info({
                restaurantCode: selectedRestaurantCode,
                message: `Audio resumed on restaurant specific audio websocket after a ${Math.round(
                  resumed.gapMs / 1000
                )}s gap`,
                sessionId: store.getState().sessionBoundary?.sessionId,
                gapMs: resumed.gapMs,
              });
            }
          }
          messages.push(data);
        }
      };

      audioMessageInterval = setInterval(() => {
        // HITL-1511: the silence watchdog rides the 100ms drain tick — armed
        // only while the socket is OPEN and a HITL session is active, so quiet
        // periods between cars never count as a gap.
        const now = Date.now();
        const activeSessionId = store.getState().sessionBoundary?.sessionId;
        audioSilenceWatchdog.setArmed(
          audioSocket?.readyState === WebSocket.OPEN &&
            Boolean(activeSessionId),
          now
        );
        const silence = audioSilenceWatchdog.tick(now);
        if (silence?.type === 'gap') {
          logger.error({
            restaurantCode: selectedRestaurantCode,
            message: `No audio received on restaurant specific audio websocket for ${Math.round(
              AUDIO_SILENCE_THRESHOLD_MS / 1000
            )}s during an active session`,
            sessionId: activeSessionId,
            silentForMs: silence.silentForMs,
          });
        }

        if (!messages.length) {
          return;
        }

        // Snapshot and reset before processing (buffer already bounded in
        // onmessage by WS-2).
        const batch = messages;
        messages = [];

        const state = store.getState();
        const { isPlaying } = state.messages;

        // WS-1 (HITL-1411): guest (ORDERBOARD) audio is played straight from
        // here and NEVER dispatched into Redux, so it no longer drives per-frame
        // reducer work + a render storm. Non-guest audio was never played, so
        // it is dropped rather than forwarded. Only non-audio events flow on.
        const filteredMessages: any[] = [];

        batch.forEach((message) => {
          if (
            taskAssignedSessionId &&
            message.session_id !== taskAssignedSessionId
          ) {
            return;
          }
          if (message.event === EventTypes.audio) {
            if (
              isGuestAudioFrame(message) &&
              isPlaying &&
              audioPlayer.audioContext
            ) {
              audioPlayer.pushBase64(message.data.payload);
            }
            return; // audio handled locally; never forwarded to Redux
          }
          filteredMessages.push(message);
        });

        // Skip the dispatch entirely when the batch was audio-only — this is the
        // bulk of flushes during active speech, and the main render-storm win.
        if (!filteredMessages.length) {
          return;
        }

        const {
          taskRouter,
          sessionBoundary,
          ai,
          config: {
            FEATURE_FLAG_FINAL_ORDER_VALIDATION,
            FEATURE_FLAG_LOYALTY_SUPPORT,
            FEATURE_FLAG_MASK_ORDER_PENDING_ERROR,
            FEATURE_FLAG_SECONDARY_ASR_ENABLED,
          },
        } = state;
        store.dispatch(
          messagingActions.messageReceived({
            messages: filteredMessages,
            taskRouter,
            sessionBoundarySessionId: sessionBoundary.sessionId,
            ai,
            FEATURE_FLAG_FINAL_ORDER_VALIDATION,
            FEATURE_FLAG_LOYALTY_SUPPORT,
            FEATURE_FLAG_MASK_ORDER_PENDING_ERROR,
            FEATURE_FLAG_SECONDARY_ASR_ENABLED,
            // HITL-1669, as above.
            isHitl7Enabled: selectIsHitl7Enabled(state),
          })
        );
      }, 100);

      audioSocket.onclose = (event: any) => {
        clearAudioWebSocketIntervals();

        if (audioSocket?.readyState === audioSocket?.CLOSED) {
          audioWebSocketConnectionTimer.stop();
        }

        handleWebSocketClose({
          event,
          isAudioWS: true,
          selectedRestaurantCode,
          onConnectionLost: () =>
            store.dispatch(messagingActions.audioWSConnectionLost()),
          onCloseConnection: () =>
            store.dispatch(messagingActions.closeAudioWSConnection()),
          retryConnection: reconnect,
        });
      };
    };

    if (messagingActions.startConnecting.match(action)) {
      eventConnect();
    }

    if (messagingActions.closeConnection.match(action)) {
      if (eventSocket) {
        // Clean up intervals before closing
        clearEventWebSocketIntervals();
        // Set onclose handler before closing to prevent reconnection attempts
        eventSocket.onclose = () => {
          logger.info({
            restaurantCode: selectedRestaurantCode,
            message:
              'Successfully closed connection to restaurant specific websocket by close-connection action',
          });
        };
        eventSocket.close();
        eventSocket = undefined;
        eventWebSocketRetries = 1;
      }
    }

    if (messagingActions.startAudioWSConnecting.match(action)) {
      audioConnect();
    }

    // WS-1 (HITL-1411): drive the audioPlayer start/stop lifecycle here, now
    // that the reducer is pure and no longer holds the player instance.
    if (messagingActions.setIsPlaying.match(action)) {
      if (action.payload) {
        audioPlayer.startAudio();
      } else {
        audioPlayer.aclose();
      }
    }

    if (messagingActions.closeAudioWSConnection.match(action)) {
      if (audioSocket) {
        // Clean up intervals before closing
        clearAudioWebSocketIntervals();
        // Set onclose handler before closing to prevent reconnection attempts
        audioSocket.onclose = () => {
          logger.info({
            restaurantCode: selectedRestaurantCode,
            message:
              'Successfully closed connection to restaurant specific audio websocket by close-connection action',
          });
        };
        audioSocket.close();
        audioSocket = undefined;
        audioWebSocketRetries = 1;
      }
    }

    if (isEventConnectionEstablished && isMatchMessagingAction(action)) {
      eventSocket?.send(JSON.stringify(action.payload));
    }

    next(action);

    if (messagingActions.messageReceived.match(action)) {
      action.payload.messages.forEach((message: any) => {
        if (message.event === EventTypes.check) {
          const requestId = message.data?.request_id;
          if (requestId) {
            buildAndDispatchOrderProcessingMetric(
              store.getState().order,
              requestId,
              store.dispatch
            );
          }
        }
      });
    }
  };
};

export let infoSeqIdCounter = new Counter();
export let hitlSessionStartSeqIdCounter = new Counter();

const messagingTransformMiddleware: Middleware = (store: RootStore) => {
  let textSeqIdCounter = 0;
  let endSessionSeqIdCounter = 0;
  let orderSeqIdCounter = 0;
  let agentCartSeqIdCounter = 0;
  let loyaltySeqIdCounter = 0;
  let ttsRequestSeqIdCounter = 0;
  let errorSeqIdCounter = 0;
  let agentFirstActivitySeqIdCounter = 0;
  let agentFirstInterceptionSeqIdCounter = 0;
  let agentInterceptionSeqIdCounter = 0;
  let agentMetricSeqIdCounter = 0;
  let agentSuggestionResponseSeqIdCounter = 0;
  let staffInterceptionSeqIdCounter = 0;
  let hitlSessionEndSeqIdCounter = 0;
  let hitlEventConnectionSeqIdCounter = 0;
  let cancelOrderSeqIdCounter = 0;

  return (next) => (action) => {
    // HITL-1612 — the one place nothing reaches the customer from.
    //
    // `messagingActions.sendMessage` is the wire sink for speech (the `text`
    // event, built in the switch below) and it has SEVEN dispatch sites. A4
    // guarded the five *functions* that reach it through `spokenPromptThunk` and
    // `useSendMessage`; four other sites roll their own raw dispatch and were
    // never covered — the centre pane's description tab and unavailable-item
    // message, the POS cart (including speaking the order total), the
    // order-failure message and legacy IntentsSection. Each repeated A4's own
    // bug: the speech goes out, THEN the interception is stamped, and A4
    // refuses the stamp rather than the speech.
    //
    // Gating here covers all seven with one condition and no origin tagging —
    // possible only because HITL now has no legitimate TTS of its own during
    // AI-driven ordering (the `/upsell` auto-prompt no longer fires for AI
    // adds, see the fix in this same ticket).
    //
    // The action is SWALLOWED, not merely kept off the wire: the reducer
    // appends to `state.sentTextFrames`, which the transcript renders, and a
    // message nobody heard must not appear there.
    if (
      messagingActions.sendMessage.match(action) &&
      !action.payload?.bypassModeGate &&
      selectIsAgentActionBlocked(store.getState())
    ) {
      logger.debug({
        message:
          'TTS refused: the agent is not in Intercept mode (HITL-1612 shadow-mode gate)',
        moreInfo: action.payload?.data?.metadata?.intent,
      });
      return;
    }

    if (isMatchMessagingAction(action)) {
      const { sessionBoundary, user, messages, restaurant, taskRouter, menu } =
        store.getState() || {};
      const { email, username, id, firstName, lastName } =
        user?.userProfile || {};

      // HITL-1612: `bypassModeGate` is a local opt-out for the two paths that
      // must still speak outside Intercept (the menu-load failure, staff
      // intent). Destructured off here because the spread below would otherwise
      // put it on the wire, where it means nothing to the consumer.
      const { bypassModeGate: _bypassModeGate, ...outboundPayload } =
        (action.payload ?? {}) as Record<string, unknown>;

      let payload: any = {
        ...outboundPayload,
        id: uuidv4(),
        session_id:
          taskRouter?.taskAssignedSessionId ||
          sessionBoundary?.sessionId ||
          sessionBoundary?.endedSessionId ||
          messages?.currentSessionId,
        ...(taskRouter.taskAssignedLaneId
          ? { lane_id: taskRouter.taskAssignedLaneId }
          : null),
        agent_type: AgentTypes.HITL,
        agent_id: agent.getAgentId(),
        timestamp: new Date().toISOString(),
        restaurant_code:
          restaurant.selectedRestaurantDetails?.restaurantCode ||
          DUMMY_RESTAURANT_CODE,
        // I would prefer not to expose this, see PRV-2631
        metadata: {
          agent_name: username,
          agent_id: id,
          agent_email: email,
          agent_first_name: firstName,
          agent_last_name: lastName,
        },
        environment: readEnvVariable('DEPLOY_ENV'),
      };

      console.time('middleware-action-switch');
      switch (action.type) {
        case messagingActions.sendMessage.type:
          console.time('middleware-sendMessage');
          payload.event = EventTypes.text;
          payload.seq = textSeqIdCounter++;
          // Add TTS priority to metadata based on intent
          const intent = action.payload?.data?.metadata?.intent;
          const priority = intent
            ? TTS_PRIORITY_BY_INTENT[intent] ?? TTS_PRIORITY_DEFAULT
            : TTS_PRIORITY_DEFAULT;
          payload.metadata = {
            ...payload.metadata,
            priority,
          };
          console.timeEnd('middleware-sendMessage');
          break;
        case messagingActions.sendEndSession.type:
          payload.event = EventTypes.endSession;
          payload.seq = endSessionSeqIdCounter++;
          break;
        case messagingActions.sendInfo.type:
          if (
            payload.data &&
            'new_language' in payload.data &&
            !payload.data.type
          ) {
            // Transform to language switch format
            payload.event = EventTypes.languageSwitch;
            payload.environment = readEnvVariable('DEPLOY_ENV') || 'PRODUCTION';
            payload.metadata = {}; // Empty metadata as per schema
          } else {
            payload.event = EventTypes.info;
          }
          payload.seq = infoSeqIdCounter.increment();
          break;
        case messagingActions.sendOrder.type:
          const { loadedVersionDetails } = menu || {};
          const {
            isBrandMenu,
            prpMenuVersion,
            prpMenuTitle,
            brandMenuVersion,
          } = loadedVersionDetails || {};

          payload.event = EventTypes.order;
          payload.seq = orderSeqIdCounter++;
          payload.metadata = {
            ...payload.metadata,
            ...(isBrandMenu
              ? {
                  brand_menu_version: brandMenuVersion + '',
                }
              : prpMenuVersion
              ? {
                  prp_menu_version: prpMenuVersion + '',
                  prp_menu_title: prpMenuTitle,
                }
              : null),
          };
          break;
        case messagingActions.sendAgentCart.type:
          payload.event = EventTypes.agentCart;
          payload.seq = agentCartSeqIdCounter++;
          break;
        case messagingActions.sendAgentFirstActivity.type:
          payload.event = EventTypes.agentFirstActivity;
          payload.seq = agentFirstActivitySeqIdCounter++;
          break;
        case messagingActions.sendAgentFirstInterception.type:
          payload.event = EventTypes.agentFirstInterception;
          payload.seq = agentFirstInterceptionSeqIdCounter++;
          break;
        case messagingActions.sendAgentInterception.type:
          payload.event = EventTypes.agentInterception;
          payload.seq = agentInterceptionSeqIdCounter++;
          break;
        case messagingActions.sendAgentMetric.type:
          payload.event = EventTypes.agentMetric;
          payload.seq = agentMetricSeqIdCounter++;
          break;
        case messagingActions.sendAgentSuggestionResponse.type:
          payload.event = EventTypes.agentSuggestionResponse;
          payload.seq = agentSuggestionResponseSeqIdCounter++;
          break;
        case messagingActions.sendStaffInterception.type:
          payload.event = EventTypes.staffIntervention;
          payload.seq = staffInterceptionSeqIdCounter++;
          break;
        case messagingActions.sendLoyalty.type:
          payload.event = EventTypes.loyalty;
          payload.seq = loyaltySeqIdCounter++;
          break;
        case messagingActions.sendTTSRequest.type:
          payload.event = EventTypes.TTSRequest;
          payload.seq = ttsRequestSeqIdCounter++;
          break;
        case messagingActions.sendError.type:
          payload.event = EventTypes.error;
          payload.seq = errorSeqIdCounter++;
          break;
        case messagingActions.sendHITLSessionStart.type:
          payload.event = EventTypes.hitlSessionStart;
          payload.seq = hitlSessionStartSeqIdCounter.increment();
          break;
        case messagingActions.sendHITLSessionEnd.type:
          payload.event = EventTypes.hitlSessionEnd;
          payload.seq = hitlSessionEndSeqIdCounter++;
          break;
        case messagingActions.sendHITLEventConnection.type:
          payload.event = EventTypes.hitlEventConnection;
          payload.seq = hitlEventConnectionSeqIdCounter++;
          break;
        case messagingActions.sendCancelOrder.type:
          payload.event = EventTypes.cancelOrder;
          payload.seq = cancelOrderSeqIdCounter++;
          break;
        default:
          break;
      }
      console.timeEnd('middleware-action-switch');
      if (!('data' in payload)) {
        payload.data = {}; // Sending empty data field to be consistent with the high level schema for all other events
      }
      action.payload = payload;
      logger.debug({
        restaurantCode:
          store.getState().restaurant?.selectedRestaurantDetails.restaurantCode,
        message: `Sending the message with event ${payload.event} via restaurant specific websocket`,
      });
    }

    next(action);
  };
};

export { messagingTransformMiddleware, socketMiddleware };
